import { useContext, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { isSameDay } from 'date-fns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography
} from '@mui/material';

import { FocusTheme } from '@/components/Focus/configs/theme';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getFocusRightsPossible, setRightsTask, setScheduleData } from '@/redux/slices/focusNewScheduleSlice';
import { selectAvailPlatform } from '@/redux/selectors/constantsSelectors';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';
import { defaultAvailEndDate } from '@/components/Focus/constants';
import { axios } from '@/_utils/utils';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import PlatformIcon from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformIcon';
import ScheduleSelectedTerritories from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleDetails/ScheduleSelectedTerritories';
import ScheduleSelectedLanguage from './ScheduleSelectedLanguage';
import RightsSelectionSection from '../NewScheduleAvails/RightsSelectionSection';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectTitlesById as selectTitlesByIdV2 } from '@/redux/selectors/titleV2Selectors';
import { selectTitleDataById } from '@/redux/selectors/titleSelectors';
import ScheduleSelections from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleSelections';
import { useRightsAvailable } from '@/components/Focus/hooks/useRightsAvailable';
import { useToast } from '@/components/_common/Toast';

const ScheduleDetails = () => {
  const dispatch = useAppDispatch();
  const selectedVersions = useAppSelector(selectVersions);
  const platformOptions = useAppSelector(selectAvailPlatform);
  const { error } = useToast();

  const {
    selectedPlatforms,
    frequency,
    startDate,
    endDate,
    selectedTerritories,
    selectedDubs,
    selectedSubs,
    selectedLicense,
    rightsTask,
    rightsPossible
  } = useAppSelector(selectFocusNewScheduleSlice);

  const hasEpisodeVersionSelected = useHasEpisodeVersionSelected();

  const { audio: dubRights, timedtext: subRights, territories: regionRights } = useRightsAvailable(rightsPossible);

  const setFrequency = (frequency: string) => {
    dispatch(setScheduleData({ frequency }));
  };
  const setStartDate = (startDate: string) => {
    dispatch(setScheduleData({ startDate }));
  };
  const setEndDate = (endDate: string) => {
    dispatch(setScheduleData({ endDate }));
  };
  const setSelectedPlatforms = (selectedPlatforms: string[]) => {
    if (selectedPlatforms.includes('all')) {
      dispatch(setScheduleData({ selectedPlatforms: Object.keys(platformOptions) }));
    } else {
      dispatch(setScheduleData({ selectedPlatforms }));
    }
  };
  const setSelectedLicense = (selectedLicense: string) => {
    dispatch(setScheduleData({ selectedLicense }));
  };

  const isDisabled = () => false;
  // const isDisabled = useMemo(
  //   () => (step: number) => {
  //     switch (step) {
  //       case 2:
  //         return selectedPlatforms.length === 0 || selectedLicense.length === 0 || !startDate;
  //       case 3:
  //         return selectedPlatforms.length === 0 || selectedLicense.length === 0 || !startDate;
  //       case 4:
  //         return selectedPlatforms.length === 0 || selectedLicense.length === 0 || !startDate;
  //     }
  //   },
  //   [selectedPlatforms, startDate, selectedLicense, rightsTask.status, rightsPossible]
  // );

  const isComplete = useMemo(
    () => (step: number) => {
      switch (step) {
        case 1:
          return Boolean(selectedLicense && selectedPlatforms.length > 0 && startDate);
        case 2:
          return Boolean(selectedLicense && selectedPlatforms.length > 0 && startDate && selectedTerritories.length > 0);
        case 3:
          return Boolean(selectedLicense && selectedPlatforms.length > 0 && startDate && selectedTerritories.length > 0 && selectedDubs.length > 0);
        case 4:
          return Boolean(
            selectedLicense &&
              selectedPlatforms.length > 0 &&
              startDate &&
              selectedTerritories.length > 0 &&
              selectedDubs.length > 0 &&
              selectedSubs.length > 0
          );
      }
    },
    [selectedPlatforms, startDate, selectedLicense, selectedTerritories, selectedDubs, selectedSubs]
  );

  const handleSelectedTerritoriesChange = (territories: string[]) => {
    dispatch(setScheduleData({ selectedTerritories: territories }));
  };

  const handleSelectedDubsChange = (dubs: string[]) => {
    dispatch(setScheduleData({ selectedDubs: dubs }));
  };

  const handleSelectedSubsChange = (subs: string[]) => {
    dispatch(setScheduleData({ selectedSubs: subs }));
  };

  const filterEndDateMinAndMaxTime = (selectedDate: Date) => {
    if (!startDate) return true;
    const dateStartDate = new Date(startDate);
    const newSelectedDate = selectedDate;
    if (isSameDay(dateStartDate, newSelectedDate)) {
      return newSelectedDate.getTime() >= dateStartDate.getTime();
    }

    return true;
  };

  useEffect(() => {
    if (rightsTask.id && rightsTask.status !== 'completed') {
      const interval = setInterval(() => {
        axios.get(`/focus/rights/tasks/${rightsTask.id}/`, { baseURL: '/api/v1.1' }).then((resp) => {
          if (resp.data?.completed_at) {
            dispatch(setRightsTask({ status: 'completed', id: rightsTask.id }));
            clearInterval(interval);
          }
          if (resp.data?.errored_at) {
            error('An error occurred while processing the rights task.');
            clearInterval(interval);
          }
        });
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [rightsTask, rightsPossible]);

  useEffect(() => {
    if (rightsTask.status === 'completed' && (!rightsPossible || Object.keys(rightsPossible.territories).length === 0)) {
      dispatch(
        getFocusRightsPossible({
          title_id: selectedVersions.map((version) => version.id),
          license: ['svod'],
          availed_platforms: ['oando'],
          term_start__gte: '2020-01-01T00:00:00Z',
          term_end__lt: defaultAvailEndDate
        })
      );
    }
  }, [selectedPlatforms, selectedLicense, startDate, endDate, rightsTask.status]);

  useEffect(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      dispatch(setScheduleData({ endDate: undefined }));
    }
  }, [startDate]);

  useEffect(() => {
    if (!endDate || !startDate) return;
    const dateStartDate = new Date(startDate);
    const dateEndDate = new Date(endDate);
    if (dateStartDate.getTime() > dateEndDate.getTime()) dispatch(setScheduleData({ endDate: undefined }));
  }, [endDate]);

  useEffect(() => {
    if (!hasEpisodeVersionSelected) {
      setFrequency('catalog');
    } else {
      setFrequency('weekly');
    }
  }, [hasEpisodeVersionSelected]);

  return (
    <>
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <StyledAccordion defaultExpanded disableGutters>
            <StyledAccordionSummary expandIcon={<ExpandIcon icon="down_filled" />}>
              <FlexBetween>
                <FlexContainer complete={isComplete(1)}>
                  <NumberCircle>{isComplete(1) ? <PolarisIcon icon="check" height={14} /> : 1}</NumberCircle>
                  <Typography>Scheduling Details</Typography>
                </FlexContainer>
                {isComplete(1) && <CompleteLabel>Complete</CompleteLabel>}
              </FlexBetween>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <DropdownSelect label="License" value={selectedLicense} disabled fullWidth required onChange={setSelectedLicense}>
                    <MenuItem value="svod">SVOD</MenuItem>
                  </DropdownSelect>
                </Grid>
                <Grid item xs={6}>
                  <Dropdown variant="filled" fullWidth>
                    <InputLabel id="platform-label" color="primary" required>
                      Platform
                    </InputLabel>
                    <Select
                      labelId="platform-label"
                      id="platform-select"
                      multiple
                      value={selectedPlatforms}
                      onChange={(e) => setSelectedPlatforms(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      fullWidth
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <PlatformIcon key={value} name={value} size="small" disableTooltip />
                          ))}
                        </Box>
                      )}
                    >
                      <MenuItem value="all">Select All</MenuItem>
                      {Object.entries(platformOptions).map((platform) => (
                        <MenuItem sx={{ display: 'flex', gap: 1 }} key={platform[0]} value={platform[0]}>
                          <PlatformIcon name={platform[0]} size="small" disableTooltip />
                          <Typography>{platform[1]}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </Dropdown>
                </Grid>
                {hasEpisodeVersionSelected && (
                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel required id="schedule-frequency-label">
                        Frequency
                      </FormLabel>
                      <StyledRadioGroup
                        aria-labelledby="schedule-frequency-label"
                        value={frequency}
                        name="radio-buttons-group"
                        onChange={(e) => setFrequency(e.target.value)}
                      >
                        <FormControlLabel value="weekly" control={<Radio />} label="Weekly" />
                        <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                        <FormControlLabel value="catalog" control={<Radio />} label="Catalog" />
                      </StyledRadioGroup>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Datepicker
                    minDate={new Date()}
                    label="Start"
                    value={startDate}
                    onChange={(date) => (date ? setStartDate(date.toISOString()) : undefined)}
                    required
                    showTimeSelect
                    timeIntervals={15}
                    timeCaption="Time"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Datepicker
                    label="End"
                    selected={endDate ? new Date(endDate) : undefined}
                    onChange={(date) => (date ? setEndDate(date.toISOString()) : undefined)}
                    disabled={!startDate}
                    showTimeSelect
                    timeIntervals={15}
                    timeCaption="Time"
                    minDate={startDate ? new Date(startDate) : undefined}
                    filterTime={filterEndDateMinAndMaxTime}
                  />
                </Grid>
              </Grid>
            </StyledAccordionDetails>
          </StyledAccordion>
          <StyledDivider>
            <DividerContent>
              <Typography variant="caption">Rights Availability Check</Typography>
              {rightsTask.status !== 'completed' && <CircularProgress size="15px" />}
            </DividerContent>
          </StyledDivider>
          <StyledAccordion disableGutters>
            <StyledAccordionSummary disabled={isDisabled(2)} expandIcon={<ExpandIcon icon="down_filled" />}>
              <FlexBetween>
                <FlexContainer complete={isComplete(2)}>
                  <NumberCircle>{isComplete(2) ? <PolarisIcon icon="check" height={14} /> : 2}</NumberCircle>
                  <Typography>Territories</Typography>
                </FlexContainer>
                {selectedTerritories.length > 0 && <CompleteLabel>Complete</CompleteLabel>}
              </FlexBetween>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <SelectedLabel>
                {selectedTerritories.length} selected of {regionRights.length} territories available
              </SelectedLabel>
              <RightsSelectionSection
                tab="territories"
                selectedRightsList={selectedTerritories}
                rightsOptions={regionRights}
                onChangeSelectedRights={handleSelectedTerritoriesChange}
                onlyRights
              />
            </StyledAccordionDetails>
          </StyledAccordion>
          <StyledAccordion disableGutters>
            <StyledAccordionSummary disabled={isDisabled(3)} expandIcon={<ExpandIcon icon="down_filled" />}>
              <FlexBetween>
                <FlexContainer complete={isComplete(3)}>
                  <NumberCircle>{isComplete(3) ? <PolarisIcon icon="check" height={14} /> : 3}</NumberCircle>
                  <Typography>Audio</Typography>
                </FlexContainer>
                {selectedDubs.length > 0 && <CompleteLabel>Complete</CompleteLabel>}
              </FlexBetween>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <SelectedLabel>
                {selectedDubs.length} selected of {dubRights.length} rights available
              </SelectedLabel>
              <RightsSelectionSection
                tab="audio"
                selectedRightsList={selectedDubs}
                rightsOptions={dubRights}
                onChangeSelectedRights={handleSelectedDubsChange}
                onlyRights
              />
            </StyledAccordionDetails>
          </StyledAccordion>
          <StyledAccordion disableGutters>
            <StyledAccordionSummary disabled={isDisabled(4)} expandIcon={<ExpandIcon icon="down_filled" />}>
              <FlexBetween>
                <FlexContainer complete={isComplete(4)}>
                  <NumberCircle>{isComplete(4) ? <PolarisIcon icon="check" height={14} /> : 4}</NumberCircle>
                  <Typography>Timed Text</Typography>
                </FlexContainer>
                {selectedSubs.length > 0 && <CompleteLabel>Complete</CompleteLabel>}
              </FlexBetween>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <SelectedLabel>
                {selectedSubs.length} selected of {subRights.length} rights available
              </SelectedLabel>
              <RightsSelectionSection
                tab="timedtext"
                selectedRightsList={selectedSubs}
                rightsOptions={subRights}
                onChangeSelectedRights={handleSelectedSubsChange}
                onlyRights
              />
            </StyledAccordionDetails>
          </StyledAccordion>
        </Grid>

        <ScheduleSelections>
          <Stack spacing={4}>
            {selectedTerritories.length > 0 && <ScheduleSelectedTerritories selectedTerritories={selectedTerritories} />}
            {selectedDubs.length > 0 && <ScheduleSelectedLanguage type="audio" selectedDubs={selectedDubs} />}
            {selectedSubs.length > 0 && <ScheduleSelectedLanguage type="subtitle" selectedSubs={selectedSubs} />}
          </Stack>
        </ScheduleSelections>
      </Grid>
    </>
  );
};

export default ScheduleDetails;

const useHasEpisodeVersionSelected = () => {
  const selectedVersions = useAppSelector(selectVersions);
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const titlesById = isTitleV2 ? useAppSelector(selectTitlesByIdV2) : useAppSelector(selectTitleDataById);

  return useMemo(
    function someVersionHasEpisodeParent() {
      return selectedVersions.some((version) => titlesById[version.lineage[0]]?.entity_type === 'episode');
    },
    [selectedVersions, isTitleV2, titlesById]
  );
};

const StyledAccordion = styled(Accordion)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  & .MuiAccordionDetails-root {
    padding: ${({ theme }) => theme.spacing(2)};
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root': {
    padding: theme.spacing(3),
    flexDirection: 'row',
    minHeight: 'auto',
    backgroundColor: (theme as FocusTheme).palette.neutral[70],
    borderRadius: 6,
    '&.Mui-expanded': {
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0
    },
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: (theme as FocusTheme).palette.neutral[80],
      color: (theme as FocusTheme).palette.neutral[40],
      '& .MuiAccordionSummary-expandIconWrapper': {
        color: (theme as FocusTheme).palette.neutral[40]
      }
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(180deg)'
    }
  }
}));

const CompleteLabel = styled(Typography)(({ theme }) => ({
  color: (theme as FocusTheme).palette.neutral[30],
  fontSize: theme.typography.pxToRem(18)
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  '&.MuiAccordionDetails-root': {
    padding: theme.spacing(3),
    backgroundColor: (theme as FocusTheme).palette.neutral[70],
    borderBottomRightRadius: 6,
    borderBottomLeftRadius: 6
  }
}));

const FlexBetween = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: space-between;
  width: 100%;
  padding-right: ${({ theme }) => theme.spacing(2)};
`;

const FlexContainer = styled.div<{ complete?: boolean }>(({ theme, complete }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  '& .MuiTypography-root': {
    fontSize: theme.typography.pxToRem(18)
  },
  '& > div': {
    color: complete ? theme.palette.secondary.main : 'inherit',
    borderColor: complete ? theme.palette.secondary.main : 'inherit'
  },
  '& > .MuiTypography-root': {
    color: complete ? theme.palette.secondary.main : 'inherit'
  }
}));

const NumberCircle = styled.div<{ disabled?: boolean }>(({ theme, disabled }) => ({
  width: 20,
  height: 20,
  borderRadius: 12,
  border: `2px solid ${disabled ? (theme as FocusTheme).palette.neutral[40] : theme.palette.text.primary}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const ExpandIcon = styled(PolarisIcon)(() => ({
  height: 10,
  width: 10
}));

const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: row;
`;

const StyledDivider = styled(Divider)`
  margin: ${({ theme }) => theme.spacing(2)} 0;
`;

const DividerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const SelectedLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(18),
  color: (theme as FocusTheme).palette.neutral[30],
  marginTop: theme.spacing(-2),
  padding: theme.spacing(0, 0, 2)
}));

const Dropdown = styled(FormControl)`
  min-width: 115px;
`;
